import {
  Building,
  Calendar,
  DollarSign,
  FileText,
  Home,
  Instagram,
  Linkedin,
  LogOut,
  Menu,
  MessageCircle,
  Receipt,
  Upload,
  X,
} from "lucide-react";
import * as React from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import logo from "../../assets/logo_no_background.png";
import { isAuthenticated, logout } from "../../services/auth.service";
import { getUserOnly } from "../../services/expositor/subscriptions.service";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import AccountMenu from "./AccountMenu";
import PasswordModal from "./PasswordModal";
import ProfileModal from "./ProfileModal";

// Navigation items configuration
const navigationItems = [
  {
    id: "home",
    title: "Início",
    icon: Home,
    path: ".",
    requiresSubscription: false,
  },
  {
    id: "community",
    title: "Comunidade",
    icon: MessageCircle,
    path: "community",
    requiresSubscription: false,
  },
  {
    id: "education",
    title: "Área Educacional",
    icon: FileText,
    path: "education",
    requiresSubscription: false,
  },
  {
    id: "events",
    title: (
      <span>
        Calendário de Eventos
        <br />
        <span className="text-xs">(fazer pré-inscrição)</span>
      </span>
    ),
    icon: Calendar,
    path: "events",
    requiresSubscription: false,
  },
  {
    id: "pre-subscriptions",
    title: "Pré-inscrições",
    icon: FileText,
    path: "pre-subscriptions",
    requiresPreSubscription: true,
  },
  {
    id: "subscriptions",
    title: "Aprovações",
    icon: Building,
    path: "subscriptions",
    requiresSubscription: true,
  },
  {
    id: "receipts",
    title: "Comprovantes",
    icon: Receipt,
    path: "receipts",
    requiresPaidSubscription: false,
  },
  {
    id: "documents",
    title: "Documentos",
    icon: FileText,
    path: "documents",
    requiresPaidSubscription: false,
  },
  {
    id: "commissions",
    title: "Comissões",
    icon: DollarSign,
    path: "commissions",
    requiresSubscription: true,
  },
  {
    id: "gallery",
    title: "Galeria",
    icon: Upload,
    path: "gallery",
    requiresPaidSubscription: false,
  },
];

export default function UserDashboard({ children }: any) {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [userSubscriptions, setUserSubscriptions] = React.useState([]);
  const [showProfileModal, setShowProfileModal] = React.useState(false);
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [userData, setUserData] = React.useState<any>({});
  const location = useLocation();

  async function syncSubscriptions() {
    setUserSubscriptions(await getUserOnly());
  }

  React.useEffect(() => {
    syncSubscriptions();
  }, []);

  if (!isAuthenticated()) {
    return <Navigate to={"/"} replace={true} />;
  }

  const preSubs =
    Array.isArray(userSubscriptions) &&
    userSubscriptions.filter((x) => x.status_id === 0);
  const subs =
    Array.isArray(userSubscriptions) &&
    userSubscriptions.filter((x) => x.status_id === 1);
  const paidSubs = subs && subs.filter((sub) => sub.is_paid);

  const shouldShowItem = (item: any) => {
    if (item.requiresPreSubscription && (!preSubs || preSubs.length === 0))
      return false;
    if (item.requiresSubscription && (!subs || subs.length === 0)) return false;
    if (item.requiresPaidSubscription && (!paidSubs || paidSubs.length === 0))
      return false;
    return true;
  };

  const isActiveRoute = (path: string) => {
    const currentPath = location.pathname.replace(/\/$/, "");
    const segments = currentPath.split("/");
    const lastSegment = segments[segments.length - 1];
    if (path === "." || path === "welcome") {
      return lastSegment === "dashboard" || lastSegment === "welcome";
    }
    return lastSegment === path;
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem("userInfo");
    window.location.href = "/";
  };

  const PRACERIA_INSTAGRAM = "praceriaoficial";
  const PRACERIA_TIKTOK = "praceria";
  const getInstagram = () => {
    const handle = PRACERIA_INSTAGRAM.replace(/^@/, "");
    const href = `https://www.instagram.com/${handle}/`;
    const display = `Instagram`;
    return { href, display };
  };
  const getTikTok = () => {
    const handle = PRACERIA_TIKTOK.replace(/^@/, "");
    const href = `https://www.tiktok.com/@${handle}`;
    const display = `TikTok`;
    return { href, display };
  };
  const getLinkedin = () => {
    const href = "https://www.linkedin.com/company/praceria";
    const display = "Linkedin";
    return { href, display };
  };
  const getFacebook = () => {
    const href = "https://www.facebook.com/praceriaoficial";
    const display = "Facebook";
    return { href, display };
  };

  return (
    <div className="max-h-screen h-full bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-50 w-80 bg-gradient-to-b from-[#24324D] to-[#1a2538] overflow-y-auto shadow-2xl transform transition-transform duration-300 ease-in-out custom-scrollbar
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0 lg:static lg:inset-0
      `}
      >
        {/* Logo and Close Button */}
        <div className="flex items-center justify-center h-32 px-6 border-b border-white/20">
          <Link to="/dashboard" className="flex items-center space-x-3">
            <img src={logo} alt="Logo" className="h-24 w-auto" />
            {/* <span className="text-xl font-bold text-white">Praceria</span> */}
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 lg:hidden text-white hover:bg-white/20"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* User Profile Section */}
        <div className="px-6 py-6 border-b border-white/20">
          <div className="flex items-center">
            <AccountMenu
              onOpenProfileModal={() => setShowProfileModal(true)}
              onOpenPasswordModal={() => setShowPasswordModal(true)}
              userData={userData}
              setUserData={setUserData}
            />
          </div>

          {/* User Stats */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <p className="text-xs text-white/70">Pré-inscrições</p>
              <p className="text-lg font-bold text-white">
                {preSubs?.length || 0}
              </p>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <p className="text-xs text-white/70">Aprovações</p>
              <p className="text-lg font-bold text-white">
                {subs?.length || 0}
              </p>
            </div>
          </div>

          {/* Social Links */}
          <div className="w-full mt-4 grid grid-cols-2 gap-x-4 gap-y-4">
            {getInstagram() && (
              <a
                href={getInstagram()!.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
              >
                <Instagram className="h-4 w-4" />
                <span className="text-sm">{getInstagram()!.display}</span>
              </a>
            )}
            {getTikTok() && (
              <a
                href={getTikTok()!.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 256 256"
                  className="h-4 w-4"
                  fill="currentColor"
                >
                  <path d="M168.2 34.8c10.9 15.6 26.5 24.3 45.4 25.1v33.4c-17.3-.9-33.9-6.9-48.3-17.1v70.8c0 36.9-29.9 66.8-66.8 66.8s-66.8-29.9-66.8-66.8c0-36.9 29.9-66.8 66.8-66.8 5.1 0 10.1.6 14.9 1.8v35.1c-4.7-2.1-9.9-3.2-15.3-3.2-17.9 0-32.5 14.6-32.5 32.5s14.6 32.5 32.5 32.5 32.5-14.6 32.5-32.5V24 h37.6z" />
                </svg>
                <span className="text-sm">{getTikTok()!.display}</span>
              </a>
            )}
            <a
              href={getLinkedin().href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
            >
              <Linkedin className="h-4 w-4" />
              <span className="text-sm">{getLinkedin().display}</span>
            </a>
            <a
              href={getFacebook().href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
              <span className="text-sm">{getFacebook().display}</span>
            </a>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1  px-4 py-6 space-y-2">
          {navigationItems.filter(shouldShowItem).map((item) => {
            const Icon = item.icon;
            const isActive = isActiveRoute(item.path);

            return (
              <Link
                key={item.id}
                to={item.path}
                className={`
                  flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group
                  ${
                    isActive
                      ? "bg-white text-[#24324D] shadow-lg"
                      : "text-white/90 hover:bg-white/10 hover:text-white"
                  }
                `}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon
                  className={`mr-3 h-5 w-5 ${
                    isActive
                      ? "text-[#24324D]"
                      : "text-white/90 group-hover:text-white"
                  }`}
                />
                {item.title}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="px-4 py-4 border-t border-white/20 space-y-2">
          <Button
            variant="ghost"
            className="w-full justify-start text-white/90 hover:bg-white/10 hover:text-white"
            onClick={handleLogout}
          >
            <LogOut className="mr-3 h-5 w-5" />
            Sair
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="overflow-y-auto flex-1 lg:ml-0">
        {/* Mobile menu button */}
        <div className="lg:hidden sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-x-4 border-b border-gray-200 bg-[#24324D] px-4 text-white">
          <Button
            variant="ghost"
            size="sm"
            className="absolute left-3 top-3"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div></div>
          <img
            src={logo}
            alt="Praceria"
            style={{
              height: 40,
              width: "auto",
            }}
          />

          <div></div>
        </div>

        {/* Page content */}
        <main className="p-2 sm:p-4 lg:p-8">
          <div className="w-full">
            <Card className="bg-white border-0 min-h-[calc(100vh-8rem)] lg:min-h-[calc(100vh-4rem)]">
              <CardContent className="p-4 md:p-6 lg:p-8">
                {children}
                <Outlet />
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      {/* Modals */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        userData={userData}
        onUserDataUpdate={(newData) => {
          setUserData(newData);
          localStorage.setItem("userInfo", JSON.stringify(newData));
        }}
      />

      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
}
